import React, { useState, useEffect, useCallback, useRef } from 'react';
import { User } from '@hanwen/shared-types';
import {
  AuthState,
  getInitialAuthState,
  apiRequest,
  handleLoginResponse,
  handleLoginError,
  handleLogout,
  handleUpdateUser,
  verifyToken,
} from './authUtils';

// 生成随机ID用于追踪useAuth实例
let instanceCounter = 0;

export const useAuth = () => {
  const instanceId = useRef(`useAuth-${++instanceCounter}`).current;
  
  // 初始状态：从localStorage读取
  const [authState, setAuthState] = useState<AuthState>(() => {
    const initialState = getInitialAuthState();
    console.log(`🏁 [${instanceId}] 从localStorage初始化认证状态，用户:`, initialState.user?.id);
    return initialState;
  });

  // 监听localStorage变化，实现跨组件同步
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'token' || e.key === 'user') {
        console.log(`🔄 [${instanceId}] localStorage变化，重新加载认证状态`);
        const newState = getInitialAuthState();
        setAuthState(newState);
      }
    };

    // 监听自定义事件（同一页面内的变化）
    const handleAuthChange = () => {
      console.log(`🔄 [${instanceId}] 认证状态变化事件，重新加载`);
      const newState = getInitialAuthState();
      setAuthState(newState);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-changed', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-changed', handleAuthChange);
    };
  }, [instanceId]);

  // API base URL - use relative path for same-origin or configure in .env
  const API_BASE = import.meta.env.VITE_API_URL || '';

  // 封装apiRequest，添加日志
  const apiRequestWithLogging = useCallback(async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    console.log(`🌐 [${instanceId}] API请求:`, { 
      url: `${API_BASE}${endpoint}`, 
      method: options.method || 'GET', 
      endpoint 
    });
    
    try {
      const result = await apiRequest<T>(endpoint, options, API_BASE, instanceId);
      console.log(`✅ [${instanceId}] API请求成功:`, endpoint);
      return result;
    } catch (error) {
      console.error(`❌ [${instanceId}] API请求失败:`, error);
      throw error;
    }
  }, [API_BASE, instanceId]);

  // 全局标志，防止短时间内重复验证
  const lastAuthCheckTime = React.useRef<number>(0);
  const AUTH_CHECK_THROTTLE_MS = 1000; // 1秒内不重复验证
  const justLoggedIn = React.useRef(false); // 标记刚登录，跳过验证

  // 验证token（仅在组件挂载时，如果已登录）
  useEffect(() => {
    // 仅在组件挂载时验证一次
    console.log(`🔄 [${instanceId}] useEffect(mount)运行，当前认证状态:`, authState);
    
    // 如果没有登录，不需要验证
    if (!authState.isAuthenticated) {
      console.log(`⏸️ [${instanceId}] 未认证，跳过token验证`);
      return;
    }
    
    // 如果刚登录，跳过验证（因为刚验证过）
    if (justLoggedIn.current) {
      console.log(`⏸️ [${instanceId}] 刚登录，跳过token验证`);
      justLoggedIn.current = false;
      return;
    }
    
    // 如果短时间内已经检查过，跳过
    const now = Date.now();
    if (now - lastAuthCheckTime.current < AUTH_CHECK_THROTTLE_MS) {
      console.log(`⏸️ [${instanceId}] 跳过认证检查（节流保护）`);
      return;
    }
    
    const verifyTokenAsync = async () => {
      console.log(`🔍 [${instanceId}] 开始验证token`);
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      if (!token || !userData) {
        console.log(`⚠️ [${instanceId}] token或user数据丢失，清除认证状态`);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        window.dispatchEvent(new Event('auth-changed'));
        lastAuthCheckTime.current = Date.now();
        return;
      }

      try {
        // 解析当前用户数据
        const currentUser = JSON.parse(userData);
        console.log(`🔍 [${instanceId}] 验证当前用户token:`, currentUser?.id);
        
        // 异步验证token
        const user = await verifyToken(token, API_BASE, instanceId);

        console.log(`✅ [${instanceId}] token验证成功，用户:`, user);
        
        // 如果验证的用户与当前的不同，更新状态
        if (JSON.stringify(user) !== JSON.stringify(currentUser)) {
          console.log(`🔄 [${instanceId}] 用户数据不一致，更新localStorage`);
          localStorage.setItem('user', JSON.stringify(user));
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          console.log(`✅ [${instanceId}] 用户数据一致，无需更新`);
        }
      } catch (error) {
        console.warn(`⚠️ [${instanceId}] Token验证失败:`, error);
        // 验证失败，清除无效token
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setAuthState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        window.dispatchEvent(new Event('auth-changed'));
        console.log(`⚠️ [${instanceId}] 清除无效token，设置未认证状态`);
      } finally {
        lastAuthCheckTime.current = Date.now();
      }
    };

    verifyTokenAsync();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 仅在挂载时运行一次

  const login = useCallback(async (email: string, password: string) => {
    console.log(`🔑 [${instanceId}] 登录开始:`, { email, timestamp: new Date().toISOString() });
    try {
      const response = await apiRequestWithLogging<{ user: User; token: string }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      console.log(`✅ [${instanceId}] 登录成功 - 用户信息:`, response.user, 'Token:', response.token);
      console.log(`📝 [${instanceId}] 更新localStorage...`);

      // 标记刚登录，防止立即验证token
      justLoggedIn.current = true;
      lastAuthCheckTime.current = Date.now();

      // 使用工具函数处理登录响应
      const user = handleLoginResponse(response, (newState) => {
        console.log(`🔄 [${instanceId}] setAuthState被调用，之前状态:`, authState);
        setAuthState(newState);
      });
      
      console.log(`✅ [${instanceId}] 认证状态已更新`, { isAuthenticated: true, user });

      return user;
    } catch (error) {
      console.error(`❌ [${instanceId}] 登录失败:`, error);
      // 使用工具函数处理登录错误
      throw handleLoginError(error);
    }
  }, [instanceId, apiRequestWithLogging]);

  const logout = useCallback(() => {
    console.log(`🚪 [${instanceId}] 用户登出`);
    handleLogout(setAuthState);
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    console.log(`✏️ [${instanceId}] 更新用户信息:`, userData);
    handleUpdateUser(userData, authState.user, setAuthState);
  }, [authState.user, instanceId]);

  // 监听authState变化
  useEffect(() => {
    console.log(`📊 [${instanceId}] authState更新:`, authState);
  }, [authState, instanceId]);

  return {
    ...authState,
    login,
    logout,
    updateUser,
  };
};